/**
 * Running a mutation, once at a time, and knowing which one is running.
 *
 * The `busy` set is keyed rather than boolean because the alternative is
 * visibly wrong on any list: publishing one article must not disable the
 * button on every other row. A single flag does exactly that, and it is the
 * most common shape of this hook in the wild.
 *
 * It does not swallow errors. The caller decides what a failure means — a
 * toast, an inline message, a rollback — and a hook that reported "failed" as
 * a boolean would take that decision away while losing the reason.
 */

import { useCallback, useRef, useState } from "react";

export interface Action {
  /** Whether this key's mutation is in flight. */
  isBusy: (key: string) => boolean;
  /** Whether any mutation is in flight. */
  busy: boolean;
  /**
   * Run `task` under `key`.
   *
   * A second call with a key already in flight is ignored rather than queued:
   * a double-clicked "Publish" is one publication, and queueing the second
   * would send it after the first had already changed the state it was
   * deciding from.
   */
  run: (key: string, task: () => Promise<unknown>) => Promise<void>;
}

export function useAction(): Action {
  const [keys, setKeys] = useState<ReadonlySet<string>>(() => new Set());
  // A ref beside the state: `run` must decide whether a key is already in
  // flight from the current value, not from the one captured when it was
  // created. Reading state in the callback would let two rapid clicks both
  // see an empty set.
  const inFlight = useRef<Set<string>>(new Set());

  const run = useCallback(async (key: string, task: () => Promise<unknown>) => {
    if (inFlight.current.has(key)) return;
    inFlight.current.add(key);
    setKeys(new Set(inFlight.current));
    try {
      await task();
    } finally {
      inFlight.current.delete(key);
      setKeys(new Set(inFlight.current));
    }
  }, []);

  return {
    isBusy: (key) => keys.has(key),
    busy: keys.size > 0,
    run,
  };
}
